import styles from './RepeatableGroup.module.css';

export interface IRepeatableBoundView {
  min?: number;
  max: number;
}

export interface RepeatableGroupProps<TInstanceId extends string = string> {
  /** Heading rendered above the instance list. */
  legend: string;
  /** Identifier list — one per instance. Order = render order. */
  instanceIds: readonly TInstanceId[];
  /**
   * Bounds derived from the catalog's `repeatableBound`. The
   * composite enforces only the visible add / remove gating; the
   * engine still owns the canonical bound enforcement.
   */
  bound: IRepeatableBoundView;
  onAddInstance: () => void;
  onRemoveInstance: (instanceId: TInstanceId) => void;
  /**
   * Render-prop for one instance. The caller decides what to render
   * for each instance (e.g., a NameComposite + a DateInput for a
   * `beneficiary.children` instance).
   */
  renderInstance: (
    instanceId: TInstanceId,
    instanceIndex: number,
  ) => React.ReactNode;
  /** Optional label for the Add Row button (defaults to "Add row"). */
  addLabel?: string;
}

/**
 * Generic repeatable-group composite. Renders an "Add row" button, a
 * list of instances rendered via `renderInstance`, and a "Remove row"
 * button per instance. Add disables at `bound.max`; Remove disables at
 * `bound.min`.
 *
 * The composite does NOT inspect the catalog's `overflowStrategy` in
 * Phase 2 batch 2 — the cascade banner / hard cap / exact-count lock
 * UX is Phase 2 Slice 7 territory and depends on the engine's
 * repeatable-state API. Until that lands, the composite is a simple
 * Add / Remove shell.
 */
export function RepeatableGroup<TInstanceId extends string = string>({
  legend,
  instanceIds,
  bound,
  onAddInstance,
  onRemoveInstance,
  renderInstance,
  addLabel = 'Add row',
}: RepeatableGroupProps<TInstanceId>): JSX.Element {
  const min = bound.min ?? 0;
  const atMax = instanceIds.length >= bound.max;
  const atMin = instanceIds.length <= min;

  // Friendlier conversational bound
  // message ("3 of 5 added" instead of "3 of up to 5"). Min hint
  // attaches as a small parenthetical when relevant.
  const boundMessage = `${instanceIds.length} of ${bound.max} added${
    min > 0 ? ` · at least ${min} required` : ''
  }`;

  return (
    <section className={styles.group}>
      <header className={styles.group__header}>
        <h3 className={styles.group__legend}>{legend}</h3>
        <span className={styles['group__bound-message']}>{boundMessage}</span>
      </header>

      <ul className={styles['group__instance-list']}>
        {instanceIds.map((instanceId, index) => (
          <li key={instanceId} className={styles.group__instance}>
            <div className={styles['group__instance-header']}>
              <span className={styles['group__instance-label']}>
                Entry {index + 1}
              </span>
              <button
                type="button"
                className={styles['group__remove-button']}
                onClick={() => onRemoveInstance(instanceId)}
                disabled={atMin}
                aria-label={`Remove entry ${index + 1}`}
              >
                Remove
              </button>
            </div>
            {renderInstance(instanceId, index)}
          </li>
        ))}
      </ul>

      <button
        type="button"
        className={styles['group__add-button']}
        onClick={onAddInstance}
        disabled={atMax}
      >
        <span aria-hidden="true" className={styles['group__add-icon']}>
          +
        </span>
        {/* Render addLabel verbatim — callers control the wording. */}
        <span>{addLabel}</span>
      </button>
    </section>
  );
}
